import { useRef, useState } from 'react';
import api from '../../../utils/api';
import { errorText } from '../../../utils/errorText';
import { submissionKeys } from '../quickActions';
import * as S from '../../specimens/styles';

/*
 * What every quick form shares: sending once (an Idempotency-Key per
 * submission), the plate and slant pickers, and the Save / Cancel row.
 */

/** Send a write with one key per submission; the error, in the server's words.
 *  `onError` sees a refusal first, for a form that must change because of it. */
export function useQuickSubmit(prefix, { onError } = {}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const keys = useRef(null);
  if (!keys.current) keys.current = submissionKeys(prefix);

  const send = async (method, path, body) => {
    setBusy(true);
    setError('');
    try {
      const res = await api[method](path, body, { headers: { 'Idempotency-Key': keys.current.keyFor(path, body) } });
      keys.current.reset();
      return res || { data: null };
    } catch (e) {
      // A refusal (4xx) is the server's answer, kept with its key: sent again
      // under that key it would only be replayed, even once its cause was
      // fixed elsewhere (a well freed, a specimen registered). The next send
      // is a new submission. With no answer, or a 5xx, the key stays, so a
      // retry is applied once.
      const status = e?.response?.status;
      if (status >= 400 && status < 500) keys.current.reset();
      setError(errorText(e));
      onError?.(e);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, send };
}

export function PlatePicker({ plates, value, onChange, t }) {
  return (
    <label style={S.label}>
      {t('quick.plate')}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={S.input}
        title={t('tip.quick.plate')} data-testid="quick-plate">
        {plates.map((p) => (
          <option key={p.plate_id} value={p.plate_id}>
            {p.plate_code} · {t(`plates:plateStatus.${p.status}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** `describe` words an option when the form needs more than code and status
 *  (Revive says where each tube is kept). */
export function SlantPicker({ slants, value, onChange, t, describe }) {
  return (
    <label style={S.label}>
      {t('quick.slant')}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={S.input}
        title={t('tip.quick.slant')} data-testid="quick-slant">
        {slants.map((s) => (
          <option key={s.slant_id} value={s.slant_id}>
            {describe ? describe(s) : `${s.slant_code} · ${t(`slants:slantStatus.${s.status}`)}`}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FormButtons({ busy, onCancel, t, saveLabel, saveTip, testId = 'quick-save' }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
      <button type="submit" disabled={busy} style={S.btn()} title={saveTip || t('tip.quick.save')} data-testid={testId}>
        {busy ? t('common:saving') : (saveLabel || t('quick.save'))}
      </button>
      <button type="button" onClick={onCancel} style={S.ghostBtn} title={t('tip.quick.cancel')}>
        {t('common:cancel')}
      </button>
    </div>
  );
}

export function FormError({ children }) {
  if (!children) return null;
  return <p role="alert" style={S.error} data-testid="quick-error">{children}</p>;
}

export const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 };
export const stack = { display: 'flex', flexDirection: 'column', gap: 10 };
